import { Text } from '#app/components/Primitives/Text.js';
import { gradientBorderBackground } from '#app/utilities/styles.js';
import { css, styled } from 'styled-components';

// a bunch of id's in this file are have hard coded values that are not valid cspell ids
// cSpell:ignore Wdvcnki

const DEFAULT_GRADIENT = css`
  background: linear-gradient(96.52deg, #ff9e31 12.52%, #ff5a5e 91.19%);
`;

const DEFAULT_BORDER_GRADIENT = gradientBorderBackground({
  colorStart: '#ff9e31',
  colorStop: '#ff5a5e',
  radius: 20,
  rotation: 0,
  strokeWidth: 3,
});

const GRADIENTS = {
  blue: {
    borderGradient: gradientBorderBackground({
      colorStart: '#6ca3e0',
      colorStop: '#ff5a5e',
      radius: 20,
      rotation: 0,
      strokeWidth: 3,
    }),
    gradient: css`
      background: linear-gradient(97.37deg, #6ca3e0 18.3%, #8f6fdb 89.73%),
        linear-gradient(96.52deg, #ff9e31 12.52%, #ff5a5e 91.19%);
    `,
  },
  default: {
    borderGradient: DEFAULT_BORDER_GRADIENT,
    gradient: DEFAULT_GRADIENT,
  },
  green: {
    borderGradient: gradientBorderBackground({
      colorStart: '#b8df1c',
      colorStop: '#ff9e31',
      radius: 20,
      rotation: 0,
      strokeWidth: 3,
    }),
    gradient: css`
      background: linear-gradient(91.71deg, #b8df1c 10.66%, #ff9e31 92.72%);
    `,
  },
  lime: {
    borderGradient: gradientBorderBackground({
      colorStart: '#c6d943',
      colorStop: '#53c1de',
      radius: 20,
      rotation: 0,
      strokeWidth: 3,
    }),
    gradient: css`
      background: linear-gradient(269.09deg, #53c1de 20.15%, #c6d943 116.25%),
        linear-gradient(91.71deg, #b8df1c 10.66%, #ff9e31 92.72%);
    `,
  },
  olive: {
    borderGradient: gradientBorderBackground({
      colorStart: '#66ad5a',
      colorStop: '#d2d943',
      radius: 20,
      rotation: 0,
      strokeWidth: 3,
    }),
    gradient: css`
      background: linear-gradient(269.09deg, #d2d943 -11.88%, #66ad5a 116.25%);
    `,
  },
  red: {
    borderGradient: gradientBorderBackground({
      colorStart: '#f98b6b',
      colorStop: '#8f6fdb',
      radius: 20,
      rotation: 0,
      strokeWidth: 3,
    }),
    gradient: css`
      background: linear-gradient(266.89deg, #8f6fdb -35.94%, #f98b6b 91.74%),
        linear-gradient(269.09deg, #53c1de 20.15%, #c6d943 116.25%),
        linear-gradient(91.71deg, #b8df1c 10.66%, #ff9e31 92.72%);
    `,
  },
  teal: {
    borderGradient: gradientBorderBackground({
      colorStart: '#51cff0',
      colorStop: '#5891d2',
      radius: 20,
      rotation: 0,
      strokeWidth: 3,
    }),
    gradient: css`
      background: linear-gradient(92.19deg, #5891d2 17.1%, #51cff0 88.21%);
    `,
  },
};

const GRADIENT_MAP = [
  {
    ...GRADIENTS.default,
    id: 'WyJCbG9nQ2F0ZWdvcnkiLDFd',
    name: 'hiring',
  },
  {
    ...GRADIENTS.default,
    id: 'WyJCbG9nQ2F0ZWdvcnkiLDExXQ==',
    name: 'Tips for Hiring',
  },
  {
    ...GRADIENTS.blue,
    id: 'WyJCbG9nQ2F0ZWdvcnkiLDJd',
    name: 'design',
  },
  {
    ...GRADIENTS.blue,
    id: 'WyJCbG9nQ2F0ZWdvcnkiLDE0XQ==',
    name: 'Design & Creative',
  },
  {
    ...GRADIENTS.green,
    id: 'WyJCbG9nQ2F0ZWdvcnkiLDNd',
    name: 'content',
  },
  {
    ...GRADIENTS.green,
    id: 'WyJCbG9nQ2F0ZWdvcnkiLDEyXQ==',
    name: 'Success Stories',
  },
  {
    ...GRADIENTS.lime,
    id: 'WyJCbG9nQ2F0ZWdvcnkiLDRd',
    name: 'engineering',
  },
  {
    ...GRADIENTS.lime,
    id: 'WyJCbG9nQ2F0ZWdvcnkiLDE1XQ==',
    name: 'Engineering, Dev & IT',
  },
  { ...GRADIENTS.red, id: 'WyJCbG9nQ2F0ZWdvcnkiLDEzXQ==', name: 'Writing' },
  {
    ...GRADIENTS.red,
    id: 'WyJCbG9nQ2F0ZWdvcnkiLDVd',
    name: 'tips',
  },
  {
    ...GRADIENTS.olive,
    id: 'WyJCbG9nQ2F0ZWdvcnkiLDZd',
    name: 'news',
  },
  { ...GRADIENTS.olive, id: 'WyJCbG9nQ2F0ZWdvcnkiLDE3XQ==', name: 'Marketing' },
  {
    ...GRADIENTS.teal,
    id: 'WyJCbG9nQ2F0ZWdvcnkiLDdd',
    name: 'community',
  },
  {
    ...GRADIENTS.teal,
    id: 'WyJCbG9nQ2F0ZWdvcnkiLDEwXQ==',
    name: 'Remote Working & Freelancing',
  },
  {
    ...GRADIENTS.default,
    id: '',
    name: 'how-to',
  },
  {
    ...GRADIENTS.olive,
    id: '',
    name: 'ai & the future of work',
  },
  {
    ...GRADIENTS.teal,
    id: '',
    name: 'tools, software and programming languages',
  },
  {
    ...GRADIENTS.red,
    id: '',
    name: 'ethics & ai',
  },
  {
    ...GRADIENTS.green,
    id: '',
    name: 'trends',
  },
];

const getBackgroundGradient = (id: string, label: string) => {
  const gradientMatch = GRADIENT_MAP.find(
    ({ id: gradientId, name }) =>
      gradientId === id || name === label.toLowerCase(),
  );
  if (gradientMatch) return gradientMatch.gradient;

  return DEFAULT_GRADIENT;
};

type TagContainerProps = TagProps & {
  $highlighted: boolean;
};

type TagProps = {
  $id: string;
  $label: string;
  $size: 'large' | 'small';
};
export const CategoryTagContainer = styled.div<TagContainerProps>`
  ${({ $highlighted, $size = 'large', theme }) => css`
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    align-items: center;
    padding: 8px 10px;
    border-radius: 20px;
    background-color: ${theme.colorsV2.white};
    box-shadow: ${theme.boxShadow.basic20};
    height: 32px;
    transition: background 350ms ease;

    ${theme.mediaQueries.lg} {
      padding: ${$size === 'small' ? '8px 10px' : '8px 12px'};
      height: ${$size === 'small' ? '32px' : '40px'};
    }

    border: 2px solid transparent;

    ${$highlighted &&
    css`
      border: 2px solid ${theme.colorsV2.gray30};
    `};

    &:hover {
      cursor: pointer;
    }
  `}
`;

export const TagLabel = styled(Text).attrs({
  textStyle: 'subtitleSmall',
})<TagProps>`
  ${({ $id, $label, $size = 'large', theme }) => css`
    ${getBackgroundGradient($id, $label)};
    font-size: ${theme.textStyles.captionSmall.fontSize};
    color: white;
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;

    &::selection {
      -webkit-text-fill-color: ${theme.colorsV2.white};
    }

    ${theme.mediaQueries.lg} {
      font-size: ${$size === 'small'
        ? theme.textStyles.captionSmall.fontSize
        : theme.textStyles.bodySmall.fontSize};
    }
  `}
`;
